import * as React from 'react';
import { createContext, useContext, useState } from 'react';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import { useTranslation } from '../../translation/TranslationContext';
import { useCreateNpsMutation } from '../nps.mutations';
import {
  CreateNpsBody,
  HandleAnswerObservationParams,
  HandleAnswerScoreParams,
  NpsContextProps,
  NpsStatusType,
  NpsViewType,
} from '../nps.types';

type NpsContextProviderProps = {
  children: React.ReactNode;
  openAsDefault?: boolean;
};

export const NpsContext = createContext<NpsContextProps>({
  isNpsOpen: true,
  isSubmitting: false,
  view: NpsViewType.ScoreForm,
  npsValues: { status: NpsStatusType.LATER },
  handleAnswerLater: () => undefined,
  handleAnswerScore: () => undefined,
  handleAnswerJump: () => undefined,
  handleAnswerObservations: () => undefined,
  onClose: () => undefined,
});

export const NpsContextProvider = ({ children }: NpsContextProviderProps) => {
  const [isNpsOpen, setIsNpsOpen] = useState(true);
  const [view, setView] = useState(NpsViewType.ScoreForm);
  const [npsValues, setNpsValues] = useState<CreateNpsBody>({ status: NpsStatusType.LATER });
  const { translate } = useTranslation();

  const onClose = () => setIsNpsOpen(false);

  const { createNps, isPending } = useCreateNpsMutation();

  const handleAnswerLater = () => {
    if (isPending) {
      return;
    }
    createNps({ status: NpsStatusType.LATER }, { onSettled: onClose });
  };

  const handleAnswerScore = ({ score }: HandleAnswerScoreParams) => {
    if (score === 0) {
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.NPS_NON_ZERO_SCORE),
      });
      return;
    }
    setNpsValues({ ...npsValues, status: NpsStatusType.OK, score: score / 10 });
    setView(NpsViewType.ContactForm);
  };

  const handleAnswerJump = () => {
    if (isPending) {
      return;
    }
    createNps(npsValues, { onSuccess: () => setView(NpsViewType.ThanksMessage) });
  };

  const handleAnswerObservations = ({ contactMe, observation }: HandleAnswerObservationParams) => {
    if (isPending) {
      return;
    }
    const body = { ...npsValues, contactMe, observation };
    setNpsValues(body);
    createNps(body, { onSuccess: () => setView(NpsViewType.ThanksMessage) });
  };

  const values: NpsContextProps = {
    isNpsOpen,
    isSubmitting: isPending,
    view,
    npsValues,
    handleAnswerLater,
    handleAnswerScore,
    handleAnswerJump,
    handleAnswerObservations,
    onClose,
  };

  return <NpsContext.Provider value={values}>{children}</NpsContext.Provider>;
};

export const useNpsContext = () => useContext<NpsContextProps>(NpsContext);
